import { z } from 'zod';

import { UuidSchema } from '../../../common/id/id';
import { makeTranslationsSchema } from '../../../common/translations/translations';

import { PermissionKeySchema } from '../../permissions/permission-key/permission-key';

/** A role's translatable content (N5) — the name staff see in their own language. */
export const RoleTranslationSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
  })
  .strict();

export type RoleTranslation = z.infer<typeof RoleTranslationSchema>;

export const RoleTranslationsSchema = makeTranslationsSchema(RoleTranslationSchema);

/**
 * A role key: stable, lower_snake, referenced by seeds, guards and the lockout
 * rules. Snake rather than the kebab of a permission key because role keys are
 * enum-like identities (`super_admin`), not namespaced paths.
 */
export const RoleKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(
    /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/,
    'A role key is lower_snake_case, e.g. "verification_clerk".',
  );

/**
 * A role as a list row: the resolved name for the caller's locale.
 *
 * `permissions` is on the **row**, not only on the detail, because a client has
 * to be able to say what a role would contribute *before* it is assigned — the
 * staff access diff attributes a pending role exactly the way it attributes an
 * assigned one, and the alternative was one detail request per newly picked role
 * (W6's N+1). It costs nothing to produce: the server reads the grouping in one
 * query for the whole page and `permissionCount` is derived from it, so the two
 * can never disagree about what the role still grants.
 */
export const RoleSchema = z.object({
  id: UuidSchema,
  key: RoleKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(PermissionKeySchema),
  permissionCount: z.number().int().min(0),
  userCount: z.number().int().min(0),
});

export type Role = z.infer<typeof RoleSchema>;

/**
 * The detail view the role editor loads: the full per-locale map its ar/en tabs
 * prefill from (W7), on top of everything the row already carries.
 */
export const RoleDetailSchema = RoleSchema.extend({
  translations: RoleTranslationsSchema,
});

export type RoleDetail = z.infer<typeof RoleDetailSchema>;

/**
 * Creating a **custom** role (`isSystem = false` always — the flag is not a
 * client-settable field, or a caller could mint an unremovable row).
 */
export const CreateRoleSchema = z
  .object({
    key: RoleKeySchema,
    translations: RoleTranslationsSchema,
    permissions: z.array(PermissionKeySchema),
  })
  .strict();

export type CreateRoleRequest = z.infer<typeof CreateRoleSchema>;

/**
 * Editing a role. The **key is immutable** and absent here on purpose: seeds,
 * guards and audit rows reference roles by key, so renaming one would silently
 * re-point them. Translations and the permission set are what change.
 */
export const UpdateRoleSchema = z
  .object({
    translations: RoleTranslationsSchema.optional(),
    permissions: z.array(PermissionKeySchema).optional(),
  })
  .strict()
  .refine(
    (value) => value.translations !== undefined || value.permissions !== undefined,
    'Nothing to update.',
  );

export type UpdateRoleRequest = z.infer<typeof UpdateRoleSchema>;
